import React, { useContext, useMemo } from "react"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  DownIcon,
  Grid,
  Link,
  Progress,
  Stack,
  Tile,
  Typography,
  GoalsIllustration
} from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import { useHistory } from "react-router"
import { useCareTeamExperts, useExpertBios } from "#/components/care-team/hooks/useCareTeamExperts"
import { getExpertSpecialty } from "#/components/carrot-plans/hooks/useJourneyOptions"
import { CarrotPlansPaths, TtcPaths } from "#/utils/Paths"
import { ExpertBio } from "#/components/read/shared/ExpertBioMapper"
import { useCurrentUser } from "#/components/context/user/UserContext"
import {
  ExpertBioModalContext,
  ExpertBioModalContextProvider,
  ExpertBioModal
} from "#/components/expert-bios/expert-bio-modal"
import { useTileEntriesFromContentful } from "#/lib/contentful/utils/useContentfulTiles"
import {
  CareTeamExpertWithTiles,
  consolidateExpertBios,
  updateExpertBiosWithTileDisplays
} from "#/components/care-team/utils/careTeamUtil"
import { useGetCarrotPlanRequestStatus } from "#/components/carrot-plans/hooks/useCarrotPlanRequestStatus"
import { useMemberActionTracking } from "#/services/memberActionTracking/useMemberActionTracking"
import { MemberActions } from "#/services/memberActionTracking/memberActions"
import useGetMemberJourneyData from "#/hooks/useGetMemberJourneyData"

const CARROT_PROFILE_IMAGE_CONTENTFUL_ENTRY_ID = "52GKCiLgbcERXPhnfDoSBR"

const NoPastSpecialistCard = ({
  expertBios,
  journeyType,
  isInAppLockdown
}: {
  expertBios: ExpertBio[]
  journeyType: string
  isInAppLockdown: boolean
}) => {
  const { formatMessage } = useIntl()
  const history = useHistory()
  const carrotPlanRequestStatus = useGetCarrotPlanRequestStatus()
  const { mutate: trackAction } = useMemberActionTracking()

  const uniqueExperts = useMemo(() => {
    const filteredExpertBios = new Map(
      expertBios
        ?.filter((bio) => bio.headshot?.id !== CARROT_PROFILE_IMAGE_CONTENTFUL_ENTRY_ID)
        .map((bio) => [bio.professionalId, bio])
    ).values()
    return Array.from(filteredExpertBios).sort(() => 0.5 - Math.random())
  }, [expertBios])

  const handleClick = () => {
    if (isInAppLockdown) {
      trackAction({
        memberActionTaken: MemberActions.CARE_TEAM_GET_CARROT_PLAN_CLICKED
      })
      history.push(CarrotPlansPaths.REDIRECT)
    } else {
      trackAction({
        memberActionTaken: MemberActions.CARE_TEAM_FIND_SPECIALIST_CLICKED
      })
      history.push(TtcPaths.TTC_LOCATION)
    }
  }
  return (
    <Card
      color="primary"
      illustration={<GoalsIllustration color="primary" />}
      image={
        <Stack
          gap={(theme) => theme.spacing(theme.tokens.spacing.md)}
          marginBottom={(theme) => theme.spacing(theme.tokens.spacing.lg)}
          direction="row"
        >
          {uniqueExperts.slice(0, 3).map((expertBio) => (
            <Box
              key={expertBio.id}
              component="img"
              src={expertBio?.headshot?.file?.url}
              alt=""
              height="4.5rem"
              width="4.5rem"
              borderRadius={(theme) => theme.tokens.borderRadius.md}
            />
          ))}
        </Stack>
      }
      title={
        !journeyType
          ? formatMessage({ defaultMessage: "Have a question about your journey?" })
          : formatMessage({ defaultMessage: "Have a question about your {journeyType} journey?" }, { journeyType })
      }
      slotProps={{ title: { sx: { maxInlineSize: "60%" } } }}
      primaryAction={
        isInAppLockdown && carrotPlanRequestStatus.data?.carrotPlanIsOnTheWay ? (
          <Typography>
            <FormattedMessage
              defaultMessage="You’ll have access to specialists as soon as your Carrot Plan is ready. In the meantime, you can explore your <link>benefit guide</link> to learn more about your benefit."
              values={{
                link: (linkContent) => (
                  <Link
                    color={"inherit"}
                    href="/your-benefit-guide"
                    target="_blank"
                    data-analytics-id="care-team-no-specialists-benefit-guide-link"
                  >
                    {linkContent}
                  </Link>
                )
              }}
            />
          </Typography>
        ) : (
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleClick}
            data-analytics-id="care-team-no-specialists-action-button"
          >
            {isInAppLockdown ? (
              <FormattedMessage defaultMessage="Get your Carrot Plan" />
            ) : (
              <FormattedMessage defaultMessage="Find a specialist" />
            )}
          </Button>
        )
      }
    >
      <Box sx={{ maxInlineSize: "60%" }}>
        <Typography>
          <FormattedMessage defaultMessage="Specialists are experts in a specific area, such as answering medical questions or offering emotional support. Schedule as many chats as you like at no cost to you." />
        </Typography>
      </Box>
    </Card>
  )
}

const ExpertElement = ({ expert }: { expert: CareTeamExpertWithTiles }) => {
  const { expertBio, services } = expert
  const { id, slug, headshot, name } = expertBio
  const history = useHistory()
  const { openExpertBioModal } = useContext(ExpertBioModalContext)
  const { mutate: trackAction } = useMemberActionTracking()

  return (
    <Stack gap={(theme) => theme.tokens.spacing.sm} direction="row">
      <Box
        component="img"
        src={headshot?.file?.url}
        alt=""
        height="4.5rem"
        width="4.5rem"
        borderRadius={(theme) => theme.tokens.borderRadius.md}
      />
      <Stack direction="column" gap={(theme) => theme.tokens.spacing.xxs}>
        <Typography variant="body2" color={(theme) => theme.palette.text.secondary}>
          <Link
            component="button"
            textAlign="start"
            color="inherit"
            data-analytics-id="care-team-specialists-expert-bio-link"
            data-expert-id={`${slug}-${id}`}
            onClick={() => openExpertBioModal({ expertBio: expertBio })}
          >
            {name}
          </Link>
        </Typography>
        {services.map((service) =>
          service.tileEntry?.title ? (
            <Typography key={service.contentfulTileEntryId} variant="body2" color="secondary">
              {service.tileEntry.title}
            </Typography>
          ) : null
        )}
        <Link
          id="moderator-textlink"
          component="button"
          textAlign="start"
          data-analytics-id="care-team-specialists-book-again-link"
          data-expert-id={`${slug}-${id}`}
          color="primary"
          onClick={() => {
            trackAction({
              memberActionTaken: MemberActions.CARE_TEAM_SPECIALIST_BOOK_AGAIN_CLICKED,
              metaData: {
                expert_name: name,
                services_offered: services.map((service) => service.tileEntry?.title).filter(Boolean)
              }
            })
            history.push({
              pathname: "/care-team/book-again",
              state: { expert }
            })
          }}
        >
          <FormattedMessage defaultMessage="Book again" />
        </Link>
      </Stack>
    </Stack>
  )
}

const FindASpecialistTile = () => {
  const history = useHistory()
  const { mutate: trackAction } = useMemberActionTracking()

  return (
    <Tile>
      <Stack direction="row" gap={(theme) => theme.tokens.spacing.md}>
        <Box
          component="img"
          src="/images/care-navigators-2x.png"
          alt="Care Navigators"
          height="3.5rem"
          width="3.5rem"
        />
        <Stack gap={(theme) => theme.tokens.spacing.md}>
          <Typography variant="h6">
            <FormattedMessage defaultMessage="Find an expert on your topic" />
          </Typography>
          <Typography variant="body1">
            <FormattedMessage defaultMessage="Specialists are experts in a specific area, such as answering medical questions or offering emotional support. Schedule as many chats as you like at no cost to you." />
          </Typography>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              trackAction({
                memberActionTaken: MemberActions.CARE_TEAM_FIND_SPECIALIST_CLICKED
              })
              history.push(TtcPaths.TTC_LOCATION)
            }}
            sx={{ inlineSize: "fit-content" }}
            data-analytics-id="care-team-find-specialist-tile-button"
          >
            <FormattedMessage defaultMessage="Find a specialist" />
          </Button>
        </Stack>
      </Stack>
    </Tile>
  )
}

const SpecialistGrid = ({ experts }: { experts: CareTeamExpertWithTiles[] }) => (
  <Grid
    container
    rowGap={(theme) => theme.tokens.spacing.xl}
    columnSpacing={2}
    direction={{ xs: "column", sm: "column", md: "row" }}
  >
    {experts.map((expert, index) => (
      <Grid key={index} item xs={12} sm={12} md={6}>
        <ExpertElement expert={expert} />
      </Grid>
    ))}
  </Grid>
)

export const CareTeamSpecialistsSection = (): JSX.Element => {
  const intl = useIntl()
  const { isInAppLockdown } = useCurrentUser()

  const { data: careTeamExperts, isLoading: isCareTeamExpertsLoading } = useCareTeamExperts()
  const { data: expertBios, isLoading: isExpertBiosLoading } = useExpertBios()
  const memberJourneyQuery = useGetMemberJourneyData()
  const consolidatedExpertBios = careTeamExperts && expertBios ? consolidateExpertBios(expertBios, careTeamExperts) : []

  const tileEntryIds = [
    ...new Set(
      consolidatedExpertBios.flatMap((expert) =>
        expert.services.map((service) => service.contentfulTileEntryId).filter(Boolean)
      )
    )
  ]

  const { data: tileDisplays } = useTileEntriesFromContentful(tileEntryIds)

  const consolidatedExpertBiosWithTileDisplay = updateExpertBiosWithTileDisplays(consolidatedExpertBios, tileDisplays)
  const journey = memberJourneyQuery?.data?.currentMemberJourney.preferSubmittedRouting.journey
  const expertSpecialty = journey ? getExpertSpecialty(journey) : null
  const formattedExpertSpecialty = journey && expertSpecialty ? intl.formatMessage(expertSpecialty) : null

  if (isCareTeamExpertsLoading || isExpertBiosLoading || memberJourneyQuery.isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <Progress />
      </Box>
    )
  }

  return (
    <Stack gap={(theme) => theme.tokens.spacing.lg}>
      <Typography variant="h3">
        <FormattedMessage defaultMessage="Specialists" />
      </Typography>

      {isInAppLockdown || consolidatedExpertBios?.length === 0 ? (
        <NoPastSpecialistCard
          expertBios={expertBios || []}
          journeyType={formattedExpertSpecialty}
          isInAppLockdown={isInAppLockdown}
        />
      ) : (
        <ExpertBioModalContextProvider>
          <Stack gap={(theme) => theme.tokens.spacing.xl}>
            <SpecialistGrid experts={consolidatedExpertBiosWithTileDisplay.slice(0, 4)} />
            {consolidatedExpertBiosWithTileDisplay.length > 4 && (
              <Accordion sx={{ "&:before": { display: "none" } }}>
                <AccordionSummary
                  expandIcon={<DownIcon fontSize="medium" />}
                  data-analytics-id="care-team-more-specialists-accordion"
                >
                  <Typography variant="h4">
                    <FormattedMessage defaultMessage="More specialists" />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <SpecialistGrid experts={consolidatedExpertBiosWithTileDisplay.slice(4)} />
                </AccordionDetails>
              </Accordion>
            )}
            <ExpertBioModal />
            <FindASpecialistTile />
          </Stack>
        </ExpertBioModalContextProvider>
      )}
    </Stack>
  )
}
